import React, { useEffect, useState } from "react";
import {
  GoogleMap,
  Marker,
  LoadScript,
  DirectionsRenderer,
  InfoWindow,
} from "@react-google-maps/api";
import {
  Clock,
  MapPin,
  Navigation2,
  TrendingUp,
  Truck,
  AlertTriangle,
  Phone,
  Mail,
  RefreshCw,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams } from "react-router-dom";
import { API_URL, GOOGLE_MAPS_API_KEY } from "./config";
import DashboardLayout from './DashboardLayout/DasboardLayout';

const calculateHaversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const toRad = (value) => (value * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in kilometers
};

const calculateETA = (distance, averageSpeed = 50) => {
  // averageSpeed in km/h
  const timeInHours = distance / averageSpeed;
  return timeInHours * 60; // Convert to minutes
};

const DashTracker = () => {
  const { tripId: initialTripId } = useParams();
  const [tripId, setTripId] = useState(initialTripId || "");
  const [tripData, setTripData] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [loadingDriver, setLoadingDriver] = useState(true);
  const [directions, setDirections] = useState(null);
  const [driver1, setDriver1] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isOffRoute, setIsOffRoute] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [distance, setDistance] = useState(0);
  const [eta, setEta] = useState(0);
  const [progress, setProgress] = useState(0);

  const customMarkers = {
    origin: {
      path: "M12 0C7.58 0 4 3.58 4 8c0 5.76 7.44 14.44 7.75 14.8a.4.4 0 0 0 .5 0C12.56 22.44 20 13.76 20 8c0-4.42-3.58-8-8-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z",
      fillColor: "#002966",
      fillOpacity: 1,
      strokeWeight: 1,
      strokeColor: "#ffffff",
      scale: 1.5,
    },
    destination: {
      path: "M12 0C7.58 0 4 3.58 4 8c0 5.76 7.44 14.44 7.75 14.8a.4.4 0 0 0 .5 0C12.56 22.44 20 13.76 20 8c0-4.42-3.58-8-8-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z",
      fillColor: "#ffc000",
      fillOpacity: 1,
      strokeWeight: 1,
      strokeColor: "#ffffff",
      scale: 1.5,
    },
    vehicle: {
      path: "M23.5 7c.276 0 .5.224.5.5v.511c0 .793-.926.989-1.616.989l-1.086-2h2.202zm-1.441 3.506c.639 1.186.946 2.252.946 3.666 0 1.37-.397 2.533-1.005 3.981v1.847c0 .552-.448 1-1 1h-1.5c-.552 0-1-.448-1-1v-1h-13v1c0 .552-.448 1-1 1h-1.5c-.552 0-1-.448-1-1v-1.847c-.608-1.448-1.005-2.611-1.005-3.981 0-1.414.307-2.48.946-3.666.829-1.537 1.851-3.453 2.93-5.252.828-1.382 1.262-1.707 2.278-1.889 1.532-.275 2.918-.365 4.851-.365s3.319.09 4.851.365c1.016.182 1.45.507 2.278 1.889 1.079 1.799 2.101 3.715 2.93 5.252zm-16.059 2.994c0-.828-.672-1.5-1.5-1.5s-1.5.672-1.5 1.5.672 1.5 1.5 1.5 1.5-.672 1.5-1.5zm10 1c0-.276-.224-.5-.5-.5h-7c-.276 0-.5.224-.5.5s.224.5.5.5h7c.276 0 .5-.224.5-.5zm2.941-5.527s-.74-1.826-1.631-3.142c-.202-.298-.515-.502-.869-.566-1.511-.272-2.835-.359-4.441-.359s-2.93.087-4.441.359c-.354.064-.667.268-.869.566-.891 1.315-1.631 3.142-1.631 3.142 1.64.313 4.309.497 6.941.497s5.301-.184 6.941-.497zm2.059 4.527c0-.828-.672-1.5-1.5-1.5s-1.5.672-1.5 1.5.672 1.5 1.5 1.5 1.5-.672 1.5-1.5zm-18.298-6.5h-2.202c-.276 0-.5.224-.5.5v.511c0 .793.926.989 1.616.989l1.086-2z",
      fillColor: "#002966",
      fillOpacity: 1,
      strokeWeight: 1,
      strokeColor: "#ffc000",
      scale: 1.2,
    },
  };

  const mapStyles = [
    {
      featureType: "all",
      elementType: "geometry",
      stylers: [{ saturation: -80 }],
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#e3f2fd" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#ffffff" }],
    },
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
  ];

  const fetchTripDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/trip/${tripId}`);
      const data = await response.json();
      setTripData(data);
    } catch (error) {
      console.error("Error fetching trip details:", error);
    }
  };

  const fetchDriverLocation = async () => {
    try {
      const response = await fetch(
        `${API_URL}/driver/${tripData[0].driver_id}`
      );
      const driverArray = await response.json();
      if (Array.isArray(driverArray) && driverArray.length > 0) {
        const driver = driverArray[0];
        setDriver1(driver);
        const lat = parseFloat(driver.lat_cordinates);
        const lng = parseFloat(driver.long_cordinates);
        if (!isNaN(lat) && !isNaN(lng)) {
          setDriverLocation({ latitude: lat, longitude: lng });
        }
      }
    } catch (error) {
      console.error("Error fetching driver location:", error);
    } finally {
      setLoadingDriver(false);
      setLastUpdate(new Date());
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (tripId) {
        await fetchTripDetails();
        await fetchDriverLocation();
      }
    };

    if (autoRefresh) {
      fetchData(); // Initial fetch
      const intervalId = setInterval(fetchData, 60000); // Refresh every 5 seconds
      return () => clearInterval(intervalId);
    }
  }, [autoRefresh, tripId]);

  useEffect(() => {
    if (mapLoaded && tripData) {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: new window.google.maps.LatLng(
            tripData[0].origin_location_lat,
            tripData[0].origin_location_long
          ),
          destination: new window.google.maps.LatLng(
            tripData[0].destination_lat,
            tripData[0].destination_long
          ),
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            setDirections(result);
          } else {
            console.error("Error fetching directions:", result);
          }
        }
      );
    }
  }, [mapLoaded, tripData]);

  const updateDistanceAndETA = () => {
    if (driverLocation && tripData?.[0]) {
      // Calculate distance from current location to destination
      const remainingDistance = calculateHaversineDistance(
        driverLocation.latitude,
        driverLocation.longitude,
        tripData[0].destination_lat,
        tripData[0].destination_long
      );

      // Calculate total trip distance
      const totalDistance = calculateHaversineDistance(
        tripData[0].origin_location_lat,
        tripData[0].origin_location_long,
        tripData[0].destination_lat,
        tripData[0].destination_long
      );

      // Calculate progress percentage
      const completedDistance = totalDistance - remainingDistance;
      const progressPercentage = (completedDistance / totalDistance) * 100;

      // Calculate ETA based on remaining distance
      const estimatedMinutes = calculateETA(remainingDistance);

      setDistance(remainingDistance);
      setEta(estimatedMinutes);
      setProgress(Math.max(0, Math.min(100, progressPercentage)));
    }
  };

  useEffect(() => {
    if (driverLocation && tripData) {
      updateDistanceAndETA();
    }
  }, [driverLocation, tripData]);

  const handleMarkerClick = (markerInfo) => {
    setSelectedMarker(markerInfo);
  };

  const center = driverLocation
    ? { lat: driverLocation.latitude, lng: driverLocation.longitude }
    : {
        lat: tripData?.[0]?.origin_location_lat || 0,
        lng: tripData?.[0]?.origin_location_long || 0,
      };

  if (!tripData || loadingDriver) {
    return (
      <DashboardLayout>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <p className="text-lg text-gray-700 dark:text-gray-300">Loading...</p>
      </div></DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <div className="min-h-screen bg-gray-50 dark:bg-[#001233] p-4 sm:p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto"
      >
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
          <div className="w-full md:w-1/2 mb-4 md:mb-0">
            <div className="relative">
              <input
                type="text"
                value={tripId}
                onChange={(e) => setTripId(e.target.value)}
                placeholder="Enter Trip ID"
                className="w-full px-4 py-2 rounded-lg border-2 border-[#002966] focus:border-[#ffc000] focus:outline-none bg-white dark:bg-gray-800 dark:text-white"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setTripId(tripId)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[#002966] text-white px-4 py-1 rounded-md hover:bg-[#001845] transition-colors"
              >
                Track
              </motion.button>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                autoRefresh
                  ? "bg-[#002966] text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              <RefreshCw
                className={`w-4 h-4 ${autoRefresh && "animate-spin"}`}
              />
              <span>Auto Refresh</span>
            </motion.button>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Last updated: {lastUpdate.toLocaleTimeString()}
            </p>
          </div>
        </div>

        <AnimatePresence>
          {isOffRoute && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-4 p-4 bg-red-100 dark:bg-red-900/50 rounded-lg border-l-4 border-red-500"
            >
              <div className="flex items-center space-x-3">
                <AlertTriangle className="w-6 h-6 text-red-500" />
                <div>
                  <p className="font-medium text-red-800 dark:text-red-200">
                    Off-Route Alert
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-300">
                    Driver has deviated from the planned route
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
          <div className="relative h-[600px]">
            <LoadScript
              googleMapsApiKey={GOOGLE_MAPS_API_KEY}
              onLoad={() => setMapLoaded(true)}
            >
              <GoogleMap
                mapContainerStyle={{ height: "100%", width: "100%" }}
                center={center}
                zoom={15}
                options={{
                  styles: mapStyles,
                  disableDefaultUI: true,
                  zoomControl: true,
                  fullscreenControl: true,
                }}
              >
                {tripData && (
                  <>
                    <Marker
                      position={{
                        lat: tripData[0].origin_location_lat,
                        lng: tripData[0].origin_location_long,
                      }}
                      icon={customMarkers.origin}
                      onClick={() =>
                        handleMarkerClick({
                          type: "origin",
                          title: "Starting Point",
                          location: tripData[0].origin_location,
                        })
                      }
                    />
                    <Marker
                      position={{
                        lat: tripData[0].destination_lat,
                        lng: tripData[0].destination_long,
                      }}
                      icon={customMarkers.destination}
                      onClick={() =>
                        handleMarkerClick({
                          type: "destination",
                          title: "Destination",
                          location: tripData[0].dest_location,
                        })
                      }
                    />
                    {driverLocation && (
                      <Marker
                        position={{
                          lat: driverLocation.latitude,
                          lng: driverLocation.longitude,
                        }}
                        icon={customMarkers.vehicle}
                        onClick={() =>
                          handleMarkerClick({
                            type: "vehicle",
                            title: "Current Location",
                            driver: driver1,
                          })
                        }
                      />
                    )}
                    {directions && (
                      <DirectionsRenderer
                        directions={directions}
                        options={{
                          suppressMarkers: true,
                          polylineOptions: {
                            strokeColor: "#002966",
                            strokeWeight: 5,
                            strokeOpacity: 0.8,
                          },
                        }}
                      />
                    )}
                    {selectedMarker && (
                      <InfoWindow
                        position={
                          selectedMarker.type === "vehicle"
                            ? {
                                lat: driverLocation.latitude,
                                lng: driverLocation.longitude,
                              }
                            : selectedMarker.type === "origin"
                            ? {
                                lat: tripData[0].origin_location_lat,
                                lng: tripData[0].origin_location_long,
                              }
                            : {
                                lat: tripData[0].destination_lat,
                                lng: tripData[0].destination_long,
                              }
                        }
                        onCloseClick={() => setSelectedMarker(null)}
                      >
                        <div className="p-2">
                          <h3 className="font-semibold text-[#002966]">
                            {selectedMarker.title}
                          </h3>
                          {selectedMarker.type === "vehicle" ? (
                            <div className="mt-2">
                              <p className="text-sm">
                                Driver: {selectedMarker.driver?.name}{" "}
                                {selectedMarker.driver?.surname}
                              </p>
                              <p className="text-sm">
                                Vehicle: {selectedMarker.driver?.model}{" "}
                                {selectedMarker.driver?.make}
                              </p>
                            </div>
                          ) : (
                            <p className="text-sm mt-1">
                              {selectedMarker.location}
                            </p>
                          )}
                        </div>
                      </InfoWindow>
                    )}
                  </>
                )}
              </GoogleMap>
            </LoadScript>
          </div>

          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-6">
            <StatusCard
              icon={<MapPin className="w-6 h-6 text-[#002966]" />}
              title="Origin"
              value={tripData[0]?.origin_location || "Loading..."}
            />
            <StatusCard
              icon={<Navigation2 className="w-6 h-6 text-[#ffc000]" />}
              title="Destination"
              value={tripData[0]?.dest_location || "Loading..."}
            />
            <StatusCard
              icon={<TrendingUp className="w-6 h-6 text-[#002966]" />}
              title="Distance Remaining"
              value={`${distance.toFixed(2)} km`}
            />
            <StatusCard
              icon={<Clock className="w-6 h-6 text-[#ffc000]" />}
              title="ETA"
              value={`${Math.round(eta)} minutes`}
            />
          </div>

          {/* Progress Bar */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="mb-2 flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Delivery Progress
              </span>
              <span className="text-sm font-medium text-[#002966] dark:text-[#ffc000]">
                {progress.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
                className="bg-[#002966] dark:bg-[#ffc000] h-2.5 rounded-full"
              />
            </div>
          </div>
        </div>
      </motion.div>
    </div></DashboardLayout>
  );
};

// Status Card Component
const StatusCard = ({ icon, title, value }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl border border-gray-200 dark:border-gray-600"
  >
    <div className="flex items-center space-x-3">
      {icon}
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        <p className="font-medium text-gray-900 dark:text-white">{value}</p>
      </div>
    </div>
  </motion.div>
);

export default DashTracker;
